import { useMemo } from 'react';
import { NJInlineMessage, NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { buildingConsumption, countModifiedRatios, fmt, totalConsumption } from './domain';
import { PC, UMR, caption } from './tokens';
import type { DjuSettings, PlanBuilding, RatioTable } from './types';

interface Props {
  buildings: PlanBuilding[];
  ratios: RatioTable;
  dju: DjuSettings;
  defaultRatios: RatioTable;
  defaultDju: DjuSettings;
  layout?: 'column' | 'row';
}

/** « Consommation globale du périmètre » : défaut → personnalisée, écart en GWh/an et en %. */
export function ImpactPanel({ buildings, ratios, dju, defaultRatios, defaultDju, layout = 'column' }: Props) {
  const base = useMemo(() => totalConsumption(buildings, defaultRatios, defaultDju), [buildings, defaultRatios, defaultDju]);
  const custom = useMemo(() => totalConsumption(buildings, ratios, dju), [buildings, ratios, dju]);
  if (base === null || custom === null) return (
    <NJInlineMessage variant="warning" scale="sm">
      <NJText scale="sm">Consommation simulée indisponible pour le périmètre complet : {fmt(buildings.filter(b =>
        buildingConsumption(b, ratios, dju).corrected === null || buildingConsumption(b, defaultRatios, defaultDju).corrected === null).length)} bâtiments sur {fmt(buildings.length)}
        sans usage Plan classé, année, surface chauffée ou ratios renseignés. Aucun total partiel n’est présenté comme un total global.</NJText>
    </NJInlineMessage>
  );
  const delta = custom - base;
  const pct = base > 0 ? (delta / base) * 100 : 0;
  const nRatios = countModifiedRatios(ratios, defaultRatios);
  const state = Math.abs(delta) < 0.5 ? 'none' : delta > 0 ? 'up' : 'down';
  const tone = {
    none: { fg: UMR.textSecondary, bg: UMR.grey100, icon: 'drag_handle', text: 'Aucun changement' },
    up: { fg: PC.warningText, bg: PC.warningBg, icon: 'trending_up', text: 'Hausse' },
    down: { fg: PC.successText, bg: PC.successBg, icon: 'trending_down', text: 'Baisse' },
  }[state];
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const row = layout === 'row';

  const value = (label: string, v: number, strong = false) => (
    <div style={{ flex: row ? 1 : undefined }}>
      <div style={caption}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: strong ? 22 : 16, lineHeight: strong ? '30px' : '24px', color: strong ? UMR.textStrong : UMR.text }}>
        {fmt(v / 1000, 1)} <span style={{ fontSize: 12, fontWeight: 400 }}>GWh/an</span>
      </div>
    </div>
  );

  return (
    <section
      aria-label="Consommation globale du périmètre"
      aria-live="polite"
      style={{
        border: `1px solid ${UMR.border}`, borderTop: `4px solid ${PC.accent}`, background: UMR.white, padding: 16,
        display: 'flex', flexDirection: 'column', gap: 12, fontFamily: UMR.font, fontSize: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UMIcon name="bolt" size={20} color={PC.accent} />
        <strong>Consommation globale simulée du périmètre</strong>
        <span style={{ ...caption, marginLeft: 'auto' }}>{fmt(buildings.length)} bâtiments</span>
      </div>
      <div style={{ display: 'flex', flexDirection: row ? 'row' : 'column', gap: row ? 24 : 8, alignItems: row ? 'center' : 'stretch' }}>
        {value('Avec les valeurs par défaut', base)}
        {row && <UMIcon name="arrow_forward" size={20} color={UMR.textSecondary} />}
        {value('Avec vos paramètres', custom, true)}
        <div
          style={{
            flex: row ? 1 : undefined, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: tone.bg, color: tone.fg,
          }}
        >
          <UMIcon name={tone.icon} size={22} color={tone.fg} />
          <div>
            <div style={{ fontSize: 12, lineHeight: '16px' }}>{tone.text}</div>
            <div style={{ fontWeight: 700 }}>
              {sign}{fmt(Math.abs(delta) / 1000, 1)} GWh/an · {sign}{fmt(Math.abs(pct), 1)} %
            </div>
          </div>
        </div>
      </div>
      <div style={{ ...caption, display: 'flex', justifyContent: 'space-between' }}>
        <span>{nRatios === 0 ? 'Aucun ratio modifié' : `${nRatios} ratio${nRatios > 1 ? 's' : ''} modifié${nRatios > 1 ? 's' : ''}`}</span>
        <span>Mis à jour en direct</span>
      </div>
    </section>
  );
}
